import { Component, computed, inject, OnInit, output } from '@angular/core';

import { extractXmlProps, getPropsFrom } from '../../../../utils/xmlParser';
import { MatIcon } from '@angular/material/icon';
import { BaseItemComponent } from '../../common-items/BaseItem/base-item-form.component';
import { BaseItemProperties, DEFAULT_BASE_PROPERTIES } from '../../common-items/BaseItem/base-item-properties';
import { DialogDrawerService } from '../../../shared/services/dialog-drawer.service';
import { LiveFormsTranslateLoaderService } from '../../../services/live-forms-translate-loader.service';

@Component({
  selector: 'lfe-repeat-group-section-item-form',
  imports: [MatIcon],
  templateUrl: './repeat-group-section-item-form.component.html',
  standalone: true,
  styleUrl: './repeat-group-section-item-form.component.css'
})
export class RepeatGroupSectionItemFormComponent extends BaseItemComponent<Record<string, any>> implements OnInit {
  addNewInstanceRequired = output<{ repeatItemId: string; repeaterItemId: string }>();
  deleteInstanceRequired = output<{ repeatGroupSectionItemId: string }>();

  private dialogDrawerService = inject(DialogDrawerService);
  private translateService = inject(LiveFormsTranslateLoaderService);

  properties: BaseItemProperties = {
    ...DEFAULT_BASE_PROPERTIES
  };

  currentId = computed(() => this.properties.customId || this.properties.id);

  htmlId = computed(() => `repeater_${this.currentId()}`);

  title = computed(() => {
    const currentRenderedItems = this.renderedXmlItems();

    const currentIdx = currentRenderedItems.findIndex((item) => {
      const props = extractXmlProps(item);

      return [props['CustomID'], props['ID']].includes(this.currentId());
    });

    const relevantItems = this.renderedXmlItems().slice(0, currentIdx).reverse();

    let qtyOfAdjacentRepeatGroupSectionItems = 1;

    for (const item of relevantItems) {
      if (item.nodeName === 'RepeatGroupSectionItem') {
        qtyOfAdjacentRepeatGroupSectionItems++;
      }

      if (item.nodeName === 'RepeatSectionItem') {
        break;
      }
    }

    return `# ${qtyOfAdjacentRepeatGroupSectionItems}`;
  });

  async onDeleteRepeatInstanceRequested(event: Event) {
    event.preventDefault();

    const result = await this.dialogDrawerService.showUserPrompt({
      title: this.translateService.instant('liveforms-editor.repeat-group-section-item.dialog.delete.title'),
      message: this.translateService.instant('liveforms-editor.repeat-group-section-item.dialog.delete.message', {
        title: this.title()
      }),
      icon: 'info',
      iconColor: '#1976d2',
      actions: [
        {
          text: this.translateService.instant('liveforms-editor.repeat-group-section-item.dialog.delete.actions.ok'),
          action: 'confirm',
          color: 'primary',
          variant: 'raised'
        },
        {
          text: this.translateService.instant(
            'liveforms-editor.repeat-group-section-item.dialog.delete.actions.cancel'
          ),
          action: 'cancel',
          color: 'primary',
          variant: 'basic'
        }
      ]
    });

    if (result === 'confirm') {
      this.deleteInstanceRequired.emit({
        repeatGroupSectionItemId: this.sectionItemId()
      });
    }
  }

  extractLocalProperties(xml: Element): BaseItemProperties {
    const props = getPropsFrom(xml);

    const customId = props['CustomID'];
    const id = props['ID'];
    const activationRule = props['ActivationRule'] || '';
    const isGenericRepeatItem = props['IsGenericRepeatItem'] === 'True';

    return {
      ...DEFAULT_BASE_PROPERTIES,
      id,
      customId,
      activationRule,
      isGenericRepeatItem
    };
  }
}
